import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useResolveHandoffMutation } from '../../hooks/useHandoff';
import type { MailFailure } from '../../types/handoff';
import { cn } from '../../utils/cn';

const KIND_LABELS: Record<MailFailure['kind'], string> = {
  handoff: 'Handoff',
  email_draft: 'Mejlutkast',
};

const KIND_COLORS: Record<MailFailure['kind'], string> = {
  handoff: 'bg-red-100 text-red-800 border-red-200',
  email_draft: 'bg-indigo-100 text-indigo-800 border-indigo-200',
};

function formatDateTime(value: string | null): string {
  if (!value) return '–';
  return new Date(value).toLocaleString('sv-SE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface MailFailureRowProps {
  failure: MailFailure;
  expanded: boolean;
  onToggle: () => void;
}

export const MailFailureRow = ({ failure, expanded, onToggle }: MailFailureRowProps) => {
  const resolveMutation = useResolveHandoffMutation();

  return (
    <>
      <tr
        onClick={onToggle}
        className={cn('cursor-pointer transition hover:bg-slate-50', expanded && 'bg-slate-50')}
      >
        <td className="px-3 py-2 text-slate-400">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>
        <td className="px-3 py-2 whitespace-nowrap tabular-nums">
          {formatDateTime(failure.requestedAt)}
        </td>
        <td className="px-3 py-2">{failure.customerName ?? 'Gäst'}</td>
        <td className="px-3 py-2 text-slate-600">{failure.channel ?? '–'}</td>
        <td className="px-3 py-2">
          <span
            className={cn(
              'inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold',
              KIND_COLORS[failure.kind],
            )}
          >
            {KIND_LABELS[failure.kind]}
          </span>
        </td>
        <td className="px-3 py-2 font-mono text-xs text-red-800">{failure.reason ?? '–'}</td>
        <td className="px-3 py-2">
          <span
            className={cn(
              'inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold',
              failure.isResolved
                ? 'bg-green-50 text-green-800 border-green-200'
                : 'bg-red-50 text-red-800 border-red-200',
            )}
          >
            {failure.isResolved ? 'Åtgärdad' : 'Öppen'}
          </span>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={7} className="bg-brand-beige-tint-2 px-4 py-4 sm:pl-10">
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Ärendet
              </h3>
              <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-xs">
                <dt className="text-slate-500">Session</dt>
                <dd className="font-mono">{failure.sessionId}</dd>
                <dt className="text-slate-500">Handoff begärd</dt>
                <dd>{formatDateTime(failure.requestedAt)}</dd>
                <dt className="text-slate-500">Mejlet föll</dt>
                <dd>{formatDateTime(failure.failedAt)}</dd>
                <dt className="text-slate-500">Ärendetyp</dt>
                <dd>{failure.caseType ?? '–'}</dd>
                {failure.isResolved && (
                  <>
                    <dt className="text-slate-500">Åtgärdad</dt>
                    <dd>{formatDateTime(failure.resolvedAt)}</dd>
                  </>
                )}
              </dl>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Link
                to={`/sessions?session=${failure.sessionId}`}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-blue px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-secondary-blue"
              >
                Öppna sessionsloggen →
              </Link>
              {!failure.isResolved && (
                <button
                  type="button"
                  onClick={() => resolveMutation.mutate(failure.sessionId)}
                  disabled={resolveMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:opacity-50"
                >
                  {resolveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Markera åtgärdad
                </button>
              )}
              {resolveMutation.isError && (
                <span className="text-xs text-red-700">
                  Kunde inte markera som åtgärdad — försök igen.
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
